import { Sprite, Vector2 } from '../../../odie';
import { IEffect } from '../../actions/effects/IEffect';
import { app } from '../../app';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { blockViewAssets } from '../../util/blockTools';
import { canSpawnBubbleOnCell } from '../../util/bubbleTools';
import { OctopusBlockEntity } from './OctopusBlockEntity';
import { config } from '../../defs/config';
import gsap from 'gsap';
import { arrayRandom } from '../../../lib/util/jsTools';
import { blockPropsMap } from '../../defs/block';
import { spawnBlockEntity, CageBlockEntity } from '../../entities/BlockEntity';
import { mapToViewPosition, mapIterateCells } from '../../util/mapTools';

export type OctopusBlockEffectOptions = {
  entity: OctopusBlockEntity;
};

export class OctopusBlockEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly options: OctopusBlockEffectOptions;
  private readonly entity: OctopusBlockEntity;

  constructor(scene: GameScene, options: OctopusBlockEffectOptions) {
    this.scene = scene;
    this.options = options;
    this.entity = options.entity;
  }

  public static assets() {
    return blockViewAssets('octopus');
  }

  public async execute() {
    //
  }

  /**
   * Find a random target position on the map for spawning a bubble block over it.
   *
   * @returns A valid target position, or undefined when none are available.
   */
  private selectTarget(): Vector2 | undefined {
    const candidates: Vector2[] = [];

    // get target candidate positions
    mapIterateCells(this.scene, (cell, position) => {
      // Cell must support a bubble overlay.
      if (!canSpawnBubbleOnCell(cell)) return;

      // Should have a base.
      if (!cell.base) return;

      // Should be breakable.
      const { blockId } = cell.base.entity.c.block;
      if (blockPropsMap[blockId].immortal) return;

      candidates.push(position);
    });

    // Return undefined if there are no valid targets
    if (candidates.length === 0) {
      return undefined;
    }

    // Pick a random one
    return arrayRandom(candidates);
  }

  /**
   * Increase the bubble (cage) goal count in current puzzle
   */
  private triggerBubbleUngoal() {
    const goalId = 'cage';

    // Retrieve the current goal count
    const count = this.scene.getGoalCount(goalId);

    // Bubble clusters will create the bubble (cage) goal out of nothing, if not set yet
    this.scene.setGoalCount(goalId, count + 1);

    // Publish the ungoal event, to nitify that the goal has been changed
    this.scene.events.publish({ id: 'ungoal', goalId });
  }

  /**
   * Spawn a bubble (cage) block at a random position on the map.
   */
  public async spawnBubble() {
    app.sound.play('puzzle-cube-dismiss');

    const target = this.selectTarget();

    // If there is no target, pop the bubble
    if (!target) return;

    // Add spawned bubbles (cage) to the goal count
    this.triggerBubbleUngoal();

    // spawn cage at target position
    const cage = spawnBlockEntity(
      this.scene,
      { id: 'cage' },
      target,
    ) as CageBlockEntity;
    const view = cage.c.view2d.view;

    // Set the cage to be invisible while animating
    view.visible = false;

    // Get the target view position
    const to = mapToViewPosition(target);

    // animate bubble throw
    await this.animateThrow(to);

    // Set the cage to be visible again
    view.visible = true;

    // play sfx
    app.sound.play('puzzle-bubble-form');

    // animate morphing
    const innerView = cage.c.blockCage.view;
    innerView.scaleX = innerView.scaleY = 1.5;

    await gsap.to(innerView, {
      duration: 0.4,
      scaleX: 1,
      scaleY: 1,
      ease: 'back.out',
    });
  }

  /**
   * Animate a little bubble being thrown towards a target position.
   */
  private async animateThrow(to: Vector2) {
    // Short delay before the animation starts
    // await gsap.to(this.entity, { delay: 0.1 });
    // Create the bubble sprite, only for the animation
    const bubble = new Sprite('blocks/octopus/bubble.png');
    const duration = 0.6;
    const octopus = this.entity;
    const octopusPosition = mapToViewPosition(octopus.c.position.mapPosition);
    const toPosition = {
      x: to.x + config.tile.size / 2,
      y: to.y + config.tile.size / 2,
    };
    const fromPosition = {
      x: octopusPosition.x + config.tile.size / 2,
      y: octopusPosition.y + config.tile.size / 2,
    };

    bubble.width = 35;
    bubble.height = 35;
    bubble.anchorX = bubble.anchorY = 0.5;
    bubble.x = fromPosition.x;
    bubble.y = fromPosition.y;
    this.scene.stage.addChild(bubble);

    // Scale up the little bubble to grow into a bubble obstacle
    gsap.to(bubble, {
      scaleX: 4,
      scaleY: 4,
      duration: duration,
      ease: 'power1.out',
    });

    // Trajectory animation
    await gsap
      .timeline()
      .to(bubble, {
        y: toPosition.y,
        duration,
        ease: 'quad.out',
      })
      .to(bubble, { x: toPosition.x, duration, ease: 'quad.inOut' }, 0)
      .to(
        bubble,
        {
          scaleX: 4,
          scaleY: 4,
          alpha: 0.7,
          duration: duration * 0.5,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        bubble,
        {
          scaleX: 3,
          scaleY: 3,
          alpha: 0.5,
          duration: duration * 0.5,
          ease: 'quad.inOut',
        },
        duration * 0.5,
      );

    // Remove the individual bubble
    this.scene.stage.removeChild(bubble);
  }

  /**
   *  Play the octopus clear animation, when the block is cleared.
   */
  public async playClearAnimation() {
    const view = this.entity.c.blockOctopus.view;
    const particles = await this.createSwimParticles();

    const particlesOverlay = spawnOverlayEntity(
      this.scene,
      this.entity.c.position.mapPosition,
      this.entity.c.block.props,
      () => particles.view,
      'foreground',
    );

    const viewOverlay = spawnOverlayEntity(
      this.scene,
      this.entity.c.position.mapPosition,
      this.entity.c.block.props,
      () => view,
      'foreground',
    );

    // const duration = 1;
    const angle = Math.random() * Math.PI * 2;
    const angleOffset = Math.PI / 2;
    const distance = 1400;
    const destX = Math.cos(angle + angleOffset) * distance;
    const destY = -Math.sin(angle + angleOffset) * distance;

    // Rotate to the shortest angle
    const currentRotation = view.rotation || 0;
    let delta = angle - currentRotation;
    // Normalize delta to [-PI, PI]
    delta = ((delta + Math.PI) % (2 * Math.PI)) - Math.PI;
    const targetRotation = currentRotation + delta;

    app.sound.play('puzzle-panther-destroy');
    app.sound.play('puzzle-roulette');
    await gsap.to(view, {
      duration: 0.2,
      rotation: targetRotation,
      ease: 'sine.out',
    });

    view.playSwimAnimation();

    const duration = 1;

    gsap.to(particles.view, {
      duration: 0.3,
      alpha: 0,
      ease: 'linear',
      delay: duration - 0.3,
    });

    gsap.to(view, {
      duration: 0.3,
      alpha: 0,
      ease: 'linear',
      delay: duration - 0.3,
    });

    app.sound.play('puzzle-bubble-destroy');
    await gsap.to(view, {
      duration,
      x: destX,
      y: destY,
      ease: 'sine.in',
      onStart: () => {
        particles.spawnX = view.x - 40;
        particles.spawnY = view.y - 40;
        particles.start();
      },
      onUpdate: () => {
        // Update particles position to follow the octopus
        particles.spawnX = view.x - 40;
        particles.spawnY = view.y - 40;
      },
    });

    particles.stop();
    despawnOverlayEntity(this.scene, viewOverlay);
    despawnOverlayEntity(this.scene, particlesOverlay);
  }

  /**
   * Create bubble particles to follow the octopus when it is cleared.
   */
  private async createSwimParticles() {
    const textures: string[] = [
      getMatch2AssetPath('blocks/octopus/bubble.png'),
    ];

    const particles = app.particles.create({
      textures,
      rate: 0.025,
      limit: 64,
      duration: 1,
      scale: [
        { x: 0.3, y: 0.3 },
        { x: 0.8, y: 0.8 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [32, 64] },
        { type: 'scale', to: 1.5 },
        { type: 'fade', to: 0 },
        { type: 'kinematic' },
      ],
    });

    return particles;
  }
}
